import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    OnInit,
} from '@angular/core';
import { SharedUiModule } from '@pf/shared-ui';
import { CpwTaskStateService, ITaskFilter, TaskFilters } from '@cpw/domain';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectOption } from '@pf/shared-common';
import {
    WarehouseFacade,
    ZoneFacade,
    ZoneHierarchyOption,
} from '@pf/platform-wms/domain';
import { FormControl } from '@angular/forms';
import { filter, map, switchMap } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { isValue } from '@pf/shared-utility';

@UntilDestroy()
@Component({
    standalone: true,
    selector: 'cpw-zone-filter',
    template: `
        <pf-select
            [placeholder]="'Filter by zone'"
            pfStyle="compact"
            [tagAmount]="2"
            [multiple]="true"
            [grouped]="true"
            [data]="zoneOptions$ | async | safeArray"
            [control]="selectControl"
            (selectedMultiple)="onFilterChange($event)"></pf-select>
    `,
    styles: [
        `
            :host {
                width: 100%;
                max-width: 400px;

                pf-select {
                    --pf-form-control-minimum-width: 100%;
                    --pf-form-control-margin-bottom: 0;
                }
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SharedUiModule, AsyncPipe],
})
export class CpwZoneFilterComponent implements ITaskFilter, OnInit {
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly taskService = inject(CpwTaskStateService);

    readonly warehouseFacade = inject(WarehouseFacade);
    readonly zoneFacade = inject(ZoneFacade);
    selectControl = new FormControl<string[]>([]);

    filter: keyof TaskFilters = 'zones';
    private optionEntries: ZoneHierarchyOption[] = [];
    private updatingFromFilter = false;

    get value() {
        return this.selectControl.value;
    }

    zoneOptions$ = this.warehouseFacade.active$.pipe(
        untilDestroyed(this),
        filter(isValue),
        switchMap(warehouse =>
            this.zoneFacade
                .searchAll$({
                    pageIndex: 1,
                    pageSize: 50,
                    filters: {
                        warehouseId: warehouse.id,
                    },
                })
                .pipe(
                    map(zones => {
                        const options =
                            this.zoneFacade.buildZoneHierarchyOptions(
                                zones,
                                true
                            );
                        this.optionEntries = options;
                        return options;
                    })
                )
        )
    );

    ngOnInit(): void {
        this.taskService.taskFilters$
            .pipe(
                untilDestroyed(this),
                map(filters => filters.zones)
            )
            .subscribe(zoneIds => {
                if (!zoneIds?.length) {
                    this.updatingFromFilter = true;
                    this.selectControl.setValue([]);
                    this.cdr.markForCheck();
                    setTimeout(() => {
                        this.updatingFromFilter = false;
                    });
                }
            });
    }

    onFilterChange(value: SelectOption[]) {
        if (this.updatingFromFilter) {
            return;
        }

        const selectedKeys = value.map(v => v.value as string);
        const allZoneIds = new Set<string>();

        for (const key of selectedKeys) {
            const entry = this.optionEntries.find(e => e.value === key);
            if (entry) {
                entry.zoneIds.forEach(id => allZoneIds.add(id));
            }
        }

        this.taskService.setFilter({
            filter: this.filter,
            value: Array.from(allZoneIds),
        });
    }
}
